import React, { useState, useEffect, useRef } from 'react';
import Phaser from 'phaser';
import { TileEditorScene } from '../game/scenes/TileEditorScene';
import { TileLayout, ObjectType, PlacedObject } from '../../../shared/types/tileLayout';

interface TileEditorPanelProps {
  onClose: () => void;
  accountInfo: any;
}

type Tool = 'place' | 'erase' | 'select' | 'pan';

const OBJECT_TYPES: { type: ObjectType; label: string; icon: string }[] = [
  { type: 'island', label: 'Island', icon: '[I]' },
  { type: 'npc', label: 'NPC Ship', icon: '[N]' },
  { type: 'resource_node', label: 'Resource Node', icon: '[R]' },
  { type: 'hazard', label: 'Hazard', icon: '[H]' },
  { type: 'structure', label: 'Structure', icon: '[S]' },
  { type: 'custom', label: 'Custom', icon: '[C]' },
];

export const TileEditorPanel: React.FC<TileEditorPanelProps> = ({ onClose, accountInfo }) => {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const sceneRef = useRef<TileEditorScene | null>(null);

  const [layouts, setLayouts] = useState<TileLayout[]>([]);
  const [currentLayout, setCurrentLayout] = useState<TileLayout | null>(null);
  const [selectedTool, setSelectedTool] = useState<Tool>('place');
  const [selectedObjectType, setSelectedObjectType] = useState<ObjectType | null>('island');
  const [selectedObject, setSelectedObject] = useState<PlacedObject | null>(null);
  const [gridVisible, setGridVisible] = useState<boolean>(true);
  const [layoutName, setLayoutName] = useState<string>('New Layout');
  const [layoutWidth, setLayoutWidth] = useState<number>(200);
  const [layoutHeight, setLayoutHeight] = useState<number>(200);

  useEffect(() => {
    if (!gameContainerRef.current) return;

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      parent: gameContainerRef.current,
      width: 800,
      height: 600,
      backgroundColor: '#1a1a2e',
      scene: TileEditorScene,
      physics: {
        default: 'arcade',
        arcade: { debug: false },
      },
    };

    gameRef.current = new Phaser.Game(config);
    
    gameRef.current.events.once('ready', () => {
      const scene = gameRef.current?.scene.getScene('TileEditorScene') as TileEditorScene;
      sceneRef.current = scene;

      if (!scene || !scene.events) {
        console.error('[TileEditor] Scene or events not available');
        return;
      }

      scene.events.on('layout:loaded', (layout: TileLayout) => {
        setCurrentLayout(layout);
        setLayoutName(layout.name);
        setLayoutWidth(layout.width);
        setLayoutHeight(layout.height);
      });

      scene.events.on('layout:modified', (layout: TileLayout) => {
        setCurrentLayout(layout);
      });

      scene.events.on('layout:saved', (layout: TileLayout) => {
        setCurrentLayout(layout);
        loadLayouts();
      });

      scene.events.on('object:selected', (obj: PlacedObject) => {
        setSelectedObject(obj);
      });

      scene.events.on('object:deselected', () => {
        setSelectedObject(null);
      });
    });

    loadLayouts();

    return () => {
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, []);

  const loadLayouts = async (): Promise<void> => {
    try {
      const response = await fetch('/api/admin/dev/tile-editor/layouts');
      const data = await response.json();
      if (data.status === 'ok') {
        setLayouts(data.layouts);
      }
    } catch (error) {
      console.error('[TileEditor] Failed to load layouts:', error);
    }
  };

  const handleToolChange = (tool: Tool): void => {
    setSelectedTool(tool);
    sceneRef.current?.events.emit('tool:change', tool);
  };

  const handleObjectTypeSelect = (type: ObjectType): void => {
    setSelectedObjectType(type);
    setSelectedTool('place');
    sceneRef.current?.events.emit('object:select', type);
  };

  const handleToggleGrid = (): void => {
    setGridVisible(!gridVisible);
    sceneRef.current?.events.emit('grid:toggle');
  };

  const handleSave = (): void => {
    sceneRef.current?.saveLayout();
  };

  const handleNew = (): void => {
    const newLayout: TileLayout = {
      id: crypto.randomUUID(),
      name: 'New Layout',
      version: 1,
      width: 200,
      height: 200,
      tileSize: 16,
      objects: [],
      metadata: {
        author: accountInfo?.username || 'admin',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        target: 'main_ocean',
      },
    };
    setCurrentLayout(newLayout);
    setLayoutName(newLayout.name);
    setLayoutWidth(newLayout.width);
    setLayoutHeight(newLayout.height);
    sceneRef.current?.events.emit('layout:update', newLayout);
  };

  const handleLoad = async (layoutId: string): Promise<void> => {
    try {
      const response = await fetch(`/api/admin/dev/tile-editor/layouts/${layoutId}`);
      const data = await response.json();
      if (data.status === 'ok') {
        setCurrentLayout(data.layout);
        setLayoutName(data.layout.name);
        setLayoutWidth(data.layout.width);
        setLayoutHeight(data.layout.height);
        sceneRef.current?.events.emit('layout:update', data.layout);
      }
    } catch (error) {
      console.error('[TileEditor] Failed to load layout:', error);
    }
  };

  const handleDelete = async (layoutId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this layout?')) return;
    
    try {
      const response = await fetch(`/api/admin/dev/tile-editor/layouts/${layoutId}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.status === 'ok') {
        loadLayouts();
        if (currentLayout?.id === layoutId) {
          handleNew();
        }
      }
    } catch (error) {
      console.error('[TileEditor] Failed to delete layout:', error);
    }
  };

  const handleExport = async (): Promise<void> => {
    if (!currentLayout) return;
    
    try {
      const response = await fetch(`/api/admin/dev/tile-editor/layouts/${currentLayout.id}/export`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentLayout.name.replace(/\s+/g, '_')}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[TileEditor] Failed to export layout:', error);
    }
  };

  const handleDeploy = async (): Promise<void> => {
    if (!currentLayout) return;
    
    const worldX = prompt('Enter world X coordinate (leave empty for main ocean):');
    const worldY = prompt('Enter world Y coordinate (leave empty for main ocean):');
    
    try {
      const response = await fetch('/api/admin/dev/tile-editor/deploy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layoutId: currentLayout.id,
          target: 'main_ocean',
          worldX: worldX ? parseInt(worldX) : undefined,
          worldY: worldY ? parseInt(worldY) : undefined,
        }),
      });
      const data = await response.json();
      if (data.status === 'ok') {
        alert(`Layout deployed successfully: ${data.message}`);
      } else {
        alert(`Deployment failed: ${data.error}`);
      }
    } catch (error) {
      console.error('[TileEditor] Failed to deploy layout:', error);
      alert('Deployment failed');
    }
  };

  const handleUpdateLayoutName = (): void => {
    sceneRef.current?.setLayoutName(layoutName);
  };

  const handleUpdateDimensions = (): void => {
    sceneRef.current?.setLayoutDimensions(layoutWidth, layoutHeight);
  };

  const handleClearLayout = (): void => {
    if (!window.confirm('Clear all objects from the layout?')) return;
    sceneRef.current?.clearLayout();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center font-seurat">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-[95vw] h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">Tile Editor</h2>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Close
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-64 bg-gray-800 p-4 overflow-y-auto border-r border-gray-700">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-white mb-2">Tools</h3>
              <div className="space-y-2">
                {[
                  { tool: 'place' as Tool, label: 'Place', icon: '[+]' },
                  { tool: 'erase' as Tool, label: 'Erase', icon: '[-]' },
                  { tool: 'select' as Tool, label: 'Select', icon: '[S]' },
                  { tool: 'pan' as Tool, label: 'Pan', icon: '[P]' },
                ].map(({ tool, label, icon }) => (
                  <button
                    key={tool}
                    onClick={() => handleToolChange(tool)}
                    className={`w-full px-3 py-2 rounded transition-colors ${
                      selectedTool === tool
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-bold text-white mb-2">Objects</h3>
              <div className="space-y-2">
                {OBJECT_TYPES.map(({ type, label, icon }) => (
                  <button
                    key={type}
                    onClick={() => handleObjectTypeSelect(type)}
                    className={`w-full px-3 py-2 rounded transition-colors ${
                      selectedObjectType === type
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-bold text-white mb-2">Settings</h3>
              <button
                onClick={handleToggleGrid}
                className="w-full px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
              >
                {gridVisible ? '[X]' : '[ ]'} Grid Visible
              </button>
            </div>

            {selectedObject && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-white mb-2">Selected Object</h3>
                <div className="bg-gray-700 rounded p-3 text-sm text-gray-300 space-y-1">
                  <div><strong>Type:</strong> {selectedObject.type}</div>
                  <div><strong>Position:</strong> ({selectedObject.tileX}, {selectedObject.tileY})</div>
                  <div><strong>Layer:</strong> {selectedObject.layer || 0}</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 flex flex-col">
            <div ref={gameContainerRef} className="flex-1 bg-gray-950" />

            <div className="bg-gray-800 p-4 border-t border-gray-700">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Layout Name</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={layoutName}
                      onChange={(e) => setLayoutName(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="flex-1 px-3 py-1 bg-gray-700 text-white rounded"
                    />
                    <button
                      onClick={handleUpdateLayoutName}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                      Update
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Dimensions (tiles)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={layoutWidth}
                      onChange={(e) => setLayoutWidth(parseInt(e.target.value))}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 bg-gray-700 text-white rounded"
                      min="10"
                      max="1000"
                    />
                    <span className="text-gray-400 self-center">×</span>
                    <input
                      type="number"
                      value={layoutHeight}
                      onChange={(e) => setLayoutHeight(parseInt(e.target.value))}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="w-20 px-2 py-1 bg-gray-700 text-white rounded"
                      min="10"
                      max="1000"
                    />
                    <button
                      onClick={handleUpdateDimensions}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                      Resize
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Objects</label>
                  <div className="text-white">
                    {currentLayout?.objects.length || 0} placed
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleNew}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  New
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded"
                  disabled={!currentLayout}
                >
                  Export
                </button>
                <button
                  onClick={handleDeploy}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded"
                  disabled={!currentLayout}
                >
                  Deploy
                </button>
                <button
                  onClick={handleClearLayout}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                  disabled={!currentLayout}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="w-64 bg-gray-800 p-4 overflow-y-auto border-l border-gray-700">
            <h3 className="text-lg font-bold text-white mb-2">Saved Layouts</h3>
            <div className="space-y-2">
              {layouts.map((layout) => (
                <div key={layout.id} className="bg-gray-700 rounded p-3">
                  <div className="text-white font-bold mb-1">{layout.name}</div>
                  <div className="text-xs text-gray-400 mb-2">
                    {layout.width}×{layout.height} • {layout.objects?.length || 0} objects
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoad(layout.id)}
                      className="flex-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleDelete(layout.id)}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {layouts.length === 0 && (
                <div className="text-gray-500 text-sm text-center py-4">
                  No saved layouts
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
